<template>
  <li
    class="list-group-item"
    :data-original-title="department.name"
    :data-department-id-str="department.departmentIdStr"
  >
    <a
      :class="`${isDeny ? 'deny' : 'allow'}`"
      ref="link"
      href="#"
      @click.prevent="onClick"
      @keydown="onKeydown"
      @keypress="onKeypress"
      @dblclick="onDblclick"
      @blur="onBlur"
    >
      <span v-for="index in department.depth" :key="index" class="indent" />
      <span v-if="isLeaf" class="indent" />
      <span
        v-else-if="department.opened"
        class="icon-chevron-down"
        @click.stop.prevent="collapseDepartment"
      />
      <span
        v-else-if="!department.opened"
        class="icon-chevron-right"
        @click.stop.prevent="expandDepartment"
      />
      <span :class="'tree-text ' + department.state" v-text="department.name" />
    </a>
  </li>
</template>

<script lang="ts">
import "../icomoon/style.css";
import type { Department } from "./values/Department";
import { VK_UP, VK_DOWN, VK_LEFT, VK_RIGHT } from "./values/VirtualKey";
import { computed, defineComponent, ref, type PropType } from "vue";

export default defineComponent({
  name: "departments-tree-node",
  props: {
    department: { type: Object as PropType<Department>, required: true },
    selectedDepartmentIdStr: { type: String, required: true },
    nextFocusHolder: { type: String, required: true },
    denyIdList: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const clickCancel = ref(0);

    const isLeaf = computed((): boolean => {
      return false;
    });

    const isDeny = computed((): boolean => {
      return props.denyIdList.includes(props.department.departmentIdStr);
    });

    const expandDepartment = (): void => {
      emit("expandDepartment", props.department.departmentIdStr);
    };

    const collapseDepartment = (): void => {
      emit("collapseDepartment", props.department.departmentIdStr);
    };

    const selectDepartment = (): void => {
      emit("selectDepartment", props.department.departmentIdStr);
    };

    const onKeydown = (event: KeyboardEvent): void => {
      // Note: event.code はIE11非対応
      switch (event.keyCode) {
        case VK_RIGHT:
          if (!props.department.opened) {
            event.preventDefault();
            expandDepartment();
          }
          break;
        case VK_LEFT:
          if (props.department.opened) {
            event.preventDefault();
            collapseDepartment();
          }
          break;
        case VK_DOWN:
          event.preventDefault();
          emit("focusNext");
          break;
        case VK_UP:
          event.preventDefault();
          emit("focusPrev");
          break;
      }
    };

    const onKeypress = (): void => {
      selectDepartment();
    };

    const onDblclick = (): void => {
      clickCancel.value = 2;
    };

    const onClick = (): void => {
      window.setTimeout(() => {
        if (clickCancel.value <= 0) {
          // シングルクリック処理
          selectDepartment();
          return;
        }
        if (clickCancel.value === 2) {
          // ダブルクリック処理
          if (props.department.opened) {
            collapseDepartment();
          } else {
            expandDepartment();
          }
        }
        clickCancel.value = clickCancel.value - 1;
      }, 150);
    };

    const onBlur = (): void => {
      emit("blur");
    };

    return {
      clickCancel,
      isLeaf,
      isDeny,
      expandDepartment,
      collapseDepartment,
      selectDepartment,
      onKeydown,
      onKeypress,
      onDblclick,
      onClick,
      onBlur,
    };
  },
});

// 要注意: Haxe 経由での JQuery からのDOM変更が残っている
</script>
<style scoped lang="scss">
$allow: #c5ebef;
$allow-hover: #9bdde2;
$deny: white;
$deny-hover: #e5e5e5;

.list-group-item {
  display: flex;
  padding: 0px;
}

a {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 10px;
  width: 100%;
  color: #34495e;

  &.deny {
    background-color: $deny;

    .selected {
      background-color: $deny-hover;
    }

    &:hover {
      background-color: $deny-hover;
    }
    &:focus {
      background-color: $deny-hover;
    }
  }

  &.allow {
    background-color: $allow;

    .selected {
      background-color: $allow-hover;
    }

    &:hover {
      background-color: $allow-hover;
    }
    &:focus {
      background-color: $allow-hover;
    }
  }
  &:focus {
    outline: none;
    text-decoration: none;
  }
  &:hover {
    text-decoration: none;
  }
}
.indent {
  margin-left: 10px;
  margin-right: 13px;
}
.icon-chevron-right {
  margin-left: 4px;
  margin-right: 5px;
}
.icon-chevron-down {
  margin-left: 4px;
  margin-right: 1px;
}
.tree-text {
  margin-left: 5px;
}
</style>
